import { Controller } from "@hotwired/stimulus"
import { AudioRecordingService } from "../services/audio_recording_service"
import { SonioxService } from "../services/soniox_service"
import { SonioxStreamingClient } from "../services/soniox_streaming_client"
import { fixWebmDuration } from "@fix-webm-duration/fix"

// AmiVoiceの代わりにSonioxでリアルタイム文字起こしを行うコントローラー。
// 既存の audio_recording_controller と完全に同じ targets / values / actions を提供し、
// HTML/UIには一切手を入れずに data-controller の付け替えだけで切り替えられる。
export default class extends Controller {
  static targets = ["startButton", "completeButton", "history", "transcriptArea", "errorMessage", "recordingStatusText", "timerBar", "timer"]
  static values = {
    examinationId: String,
    engine: String,           // 未使用（既存と互換のため受ける）
    profile: String,          // 未使用（既存と互換のため受ける）
    hasRecordings: Boolean,
    hasMemo: Boolean,
    qrEnabled: Boolean,
    terms: { type: Array, default: [] }
  }
  static outlets = ["recording-header"]

  // Transcript表示/非表示制御（outletsから呼ばれる）
  updateTranscriptVisibility(isVisible) {
    if (this.hasTranscriptAreaTarget) {
      if (isVisible) {
        this.transcriptAreaTarget.classList.remove('hidden')
      } else {
        this.transcriptAreaTarget.classList.add('hidden')
      }
    }
  }

  connect() {
    // 録音関連の状態
    this.startedAt = null
    this.stream = null
    this.mediaRecorder = null
    this.recordedChunks = []
    this.recordingSessionId = null
    this.recordingCompleted = false
    this.isGeneratingSoap = false
    this.isCompleteRecording = false
    this.currentSessionTexts = []
    this.sonioxClient = null
    this.isActive = false
    this.partialEl = null

    // タイマー関連
    this.timerInterval = null
    this.recordingStartTime = null

    // 遷移防止のイベントリスナーを設定
    this.setupNavigationPrevention()

    // 初期ボタン状態を設定（録音なし状態で初期化）
    this.updateButtonState(false)

    // URLパラメータをチェックして自動録音開始
    this.checkAutoStart()
  }

  disconnect() {
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
  }

  async checkAutoStart() {
    const urlParams = new URLSearchParams(window.location.search)
    const autoStart = urlParams.get('auto_start')

    if (autoStart !== 'true') {
      return
    }

    // URLパラメータをクリア
    const newUrl = window.location.pathname + window.location.search.replace(/[?&]auto_start=true/, '')
    window.history.replaceState({}, '', newUrl)

    const autoRecordEnabled = this.getAutoRecordSetting()
    if (autoRecordEnabled !== 'true') {
      return
    }

    this.showLoading("録音準備中...")
    const result = await this.startRecordingWithRetry(3)

    if (result.success) {
      this.hideLoading()
      this.showSuccess("録音を開始しました")
    } else {
      this.hideLoading()
      this.showError("録音の自動開始に失敗しました。録音ボタンを押して手動で開始してください。")
      this.highlightRecordingButton()
      setTimeout(() => this.removeHighlight(), 10000)
    }
  }

  async startRecording() {
    this.removeHighlight()

    if (this.isActive) {
      // 録音停止（一時停止と同じ扱い：完全停止して MediaRecorder の onstop に進める）
      this.startButtonTarget.disabled = true
      await this._stopSoniox()
      this.stopMediaRecorder()
      return
    }

    // 録音開始
    this.startButtonTarget.disabled = true
    this.startedAt = new Date().toISOString()
    this.currentSessionTexts = []
    this.recordingSessionId = crypto.randomUUID()
    this.recordingCompleted = false
    console.log('[Soniox Recording Session Started]', { sessionId: this.recordingSessionId })

    try {
      // 音声ストリーム取得
      if (!this.stream) {
        await this.setupAudioStream()
      }

      // Sonioxの短命APIキーを取得
      const apiKey = await SonioxService.fetchAuthToken()
      if (!apiKey) {
        throw new Error("Soniox認証トークンの取得に失敗しました")
      }

      // Sonioxストリーミング開始
      await this._startSoniox(apiKey)

      // MediaRecorder（音声ファイル保存用）開始
      this.setupMediaRecorder()

      this.isActive = true
      this.updateButtonState(true)
    } catch (error) {
      this.startButtonTarget.disabled = false
      this.log(`録音開始エラー: ${error.message}`)
      this.showError(`録音開始に失敗しました: ${error.message}`)
      // 失敗時は後始末
      await this._stopSoniox()
      this._releaseStream()
    }
  }

  stopRecording() {
    if (this.isActive) {
      this._stopSoniox()
      this.stopMediaRecorder()
    }
  }

  async completeRecording() {
    if (this.isActive) {
      // 録音中の場合：録音停止 → MediaRecorder の onstop で保存処理
      this.disableCompleteButton()
      this.showLoading("カルテを作成中...")
      this.isCompleteRecording = true
      await this._stopSoniox()
      this.stopMediaRecorder()
    } else {
      // 録音していない場合：既存の録音・メモからSOAP生成
      this.disableCompleteButton()
      this.showLoading("カルテを作成中...")
      await this.generateSoapWithoutRecording()
    }
  }

  disableCompleteButton() {
    this.isGeneratingSoap = true
    this.completeButtonTarget.disabled = true
    this.completeButtonTarget.style.opacity = "0.4"
    this.completeButtonTarget.style.cursor = "not-allowed"
  }

  enableCompleteButton() {
    this.isGeneratingSoap = false
    this.completeButtonTarget.disabled = false
    this.completeButtonTarget.style.opacity = "1"
    this.completeButtonTarget.style.cursor = "pointer"
  }

  async generateSoapWithoutRecording() {
    try {
      const templateId = localStorage.getItem('device_soap_prompt_template_id') ||
        document.querySelector('[data-soap-prompt-selector-target="select"]')?.value
      const body = templateId ? JSON.stringify({ soap_prompt_template_id: templateId }) : JSON.stringify({})

      const response = await fetch(`/examinations/${this.examinationIdValue}/generate_soap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: body
      })

      const result = await response.json()

      if (result.success) {
        if (this.qrEnabledValue) {
          window.location.href = window.location.pathname + '?show_qr=1'
        } else {
          window.location.reload()
        }
      } else {
        this.hideLoading()
        alert('カルテ生成に失敗しました: ' + result.error)
        this.enableCompleteButton()
      }
    } catch (error) {
      this.hideLoading()
      console.error('SOAP生成エラー:', error)
      alert('カルテ生成中にエラーが発生しました')
      this.enableCompleteButton()
    }
  }

  updateButtonState(isRecording) {
    if (this.hasRecordingHeaderOutlet) {
      this.recordingHeaderOutlet.setRecordingState(isRecording)
    }

    this.updateRecordingStatus(isRecording)
    this.updateTimerBar(isRecording)

    if (this.hasStartButtonTarget) {
      this.startButtonTarget.disabled = false
      const startSpan = this.startButtonTarget.querySelector('span')
      const iconDiv = this.startButtonTarget.querySelector('div')

      if (startSpan) {
        startSpan.textContent = isRecording ? "一時停止" : "録音開始"
      }

      if (iconDiv) {
        if (isRecording) {
          iconDiv.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" fill="white"/>
              <rect x="7" y="6" width="2" height="8" fill="#FF0032"/>
              <rect x="11" y="6" width="2" height="8" fill="#FF0032"/>
            </svg>
          `
          iconDiv.className = ''
          iconDiv.style.backgroundColor = ''
        } else {
          iconDiv.innerHTML = ''
          iconDiv.className = 'w-4 h-4 rounded-full'
          iconDiv.style.backgroundColor = '#FF0032'
        }
      }

      if (isRecording) {
        this.startButtonTarget.style.backgroundColor = "#FF0032"
        this.startButtonTarget.style.color = "#FFFFFF"
      } else {
        this.startButtonTarget.style.backgroundColor = "#F4F2F2"
        this.startButtonTarget.style.color = "#666666"
      }
    }

    if (this.hasCompleteButtonTarget && !this.isGeneratingSoap) {
      const canComplete = isRecording || this.hasRecordingsValue || this.hasMemoValue
      this.completeButtonTarget.disabled = !canComplete

      if (canComplete) {
        this.completeButtonTarget.style.opacity = "1"
        this.completeButtonTarget.style.cursor = "pointer"
      } else {
        this.completeButtonTarget.style.opacity = "0.4"
        this.completeButtonTarget.style.cursor = "not-allowed"
      }
    }
  }

  updateRecordingStatus(isRecording) {
    if (!this.hasRecordingStatusTextTarget) return
    if (isRecording) {
      this.recordingStatusTextTarget.classList.remove('hidden')
    } else {
      this.recordingStatusTextTarget.classList.add('hidden')
    }
  }

  updateTimerBar(isRecording) {
    if (!this.hasTimerBarTarget) return
    if (isRecording) {
      this.timerBarTarget.classList.remove('hidden')
      this.startTimer()
    } else {
      this.timerBarTarget.classList.add('hidden')
      this.stopTimer()
    }
  }

  scrollToBottom() {
    if (this.hasHistoryTarget) {
      this.historyTarget.scrollTop = this.historyTarget.scrollHeight
    }
  }

  startTimer() {
    this.recordingStartTime = Date.now()
    this.updateTimerDisplay()
    this.timerInterval = setInterval(() => this.updateTimerDisplay(), 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
    this.recordingStartTime = null
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = "00:00"
    }
  }

  updateTimerDisplay() {
    if (!this.recordingStartTime || !this.hasTimerTarget) return
    const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000)
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0')
    const seconds = (elapsed % 60).toString().padStart(2, '0')
    this.timerTarget.textContent = `${minutes}:${seconds}`
  }

  log(message) {
    console.log("[SonioxRecording]", message)
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
    }
    setTimeout(() => this.hideError(), 5000)
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
    }
  }

  showLoading(message = "録音を準備中...") {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
      this.errorMessageTarget.classList.remove('text-red-600', 'border-red-200')
      this.errorMessageTarget.classList.add('text-blue-600', 'border-blue-200')
    }
  }

  hideLoading() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
      this.errorMessageTarget.classList.remove('text-blue-600', 'border-blue-200')
      this.errorMessageTarget.classList.add('text-red-600', 'border-red-200')
    }
  }

  showSuccess(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden', 'text-red-600', 'border-red-200', 'text-blue-600', 'border-blue-200')
      this.errorMessageTarget.classList.add('text-green-600', 'border-green-200')
    }
    setTimeout(() => this.hideSuccess(), 3000)
  }

  hideSuccess() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
      this.errorMessageTarget.classList.remove('text-green-600', 'border-green-200')
      this.errorMessageTarget.classList.add('text-red-600', 'border-red-200')
    }
  }

  highlightRecordingButton() {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.style.animation = 'pulse 1.5s ease-in-out infinite'
      if (!document.getElementById('recording-pulse-animation')) {
        const style = document.createElement('style')
        style.id = 'recording-pulse-animation'
        style.textContent = `
          @keyframes pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25, 186, 143, 0.7); }
            50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(25, 186, 143, 0); }
          }
        `
        document.head.appendChild(style)
      }
    }
  }

  removeHighlight() {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.style.animation = ''
    }
  }

  async ensureMicrophoneSelected() {
    const selectedDeviceId = localStorage.getItem('selectedMicrophoneId')
    if (selectedDeviceId) return true

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = devices.filter(device => device.kind === 'audioinput')
      if (audioInputs.length === 0) return false

      const firstMic = audioInputs[0]
      localStorage.setItem('selectedMicrophoneId', firstMic.deviceId)

      const micSelect = document.getElementById('microphone-select')
      if (micSelect) {
        micSelect.value = firstMic.deviceId
      }
      return true
    } catch (_error) {
      return false
    }
  }

  async startRecordingWithRetry(maxRetries = 3) {
    const delays = [1000, 2000, 3000]
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const micSelected = await this.ensureMicrophoneSelected()
        if (!micSelected) throw new Error("マイクの選択に失敗しました")

        await this.startRecording()
        return { success: true }
      } catch (error) {
        if (attempt === maxRetries - 1) {
          return { success: false, error: error.message, attempts: maxRetries }
        }
        const delay = delays[attempt] || 2000
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    return { success: false, error: "最大試行回数に達しました" }
  }

  async setupAudioStream() {
    try {
      const selectedDeviceId = localStorage.getItem('selectedMicrophoneId')

      if (!selectedDeviceId) {
        this.showError('マイクが選択されていません。ヘッダーからマイクを選択してください。')
        throw new Error('Microphone not selected')
      }

      // 検証目的: AmiVoiceと同じ入力条件で精度比較するため、ブラウザの音声前処理（AEC/NS/AGC）をすべて無効化する。
      // これにより、PC内部音声（YouTubeなど）もマイクに乗るようになる。
      // TODO: 本番採用時は要再検討。echoCancellation/noiseSuppression/autoGainControl を有効にすると認識精度が上がる可能性あり。
      const constraints = {
        audio: {
          deviceId: selectedDeviceId === 'default' ? undefined : { exact: selectedDeviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      }

      this.stream = await navigator.mediaDevices.getUserMedia(constraints)
    } catch (error) {
      if (error.message === 'Microphone not selected') throw error

      if (['NotFoundError', 'DeviceNotFoundError', 'NotReadableError', 'OverconstrainedError'].includes(error.name)) {
        localStorage.removeItem('selectedMicrophoneId')
        this.showError('選択されたマイクにアクセスできません。ヘッダーからマイクを選択し直してください。')
      } else {
        this.showError('音声の取得に失敗しました。マイクの権限を確認してください。')
      }
      this.log(`音声ストリーム取得エラー: ${error.message}`)
      throw error
    }
  }

  setupMediaRecorder() {
    if (!this.stream) return

    const mimeType = 'audio/mp4'
    console.log('[MediaRecorder mimeType]', { mimeType, isSupported: MediaRecorder.isTypeSupported(mimeType) })

    this.mediaRecorder = new MediaRecorder(this.stream, { mimeType })
    this.recordedChunks = []
    this.recordingMimeType = mimeType

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data)
      }
    }

    this.mediaRecorder.onstop = async () => {
      await this.handleRecordingComplete()
    }

    this.mediaRecorder.start(1000) // Safari対策: 1秒ごとに dataavailable
  }

  stopMediaRecorder() {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
    }
  }

  async handleRecordingComplete() {
    const sessionId = this.recordingSessionId
    if (this.recordingCompleted) {
      console.warn('[Soniox handleRecordingComplete duplicate prevented]', { sessionId })
      return
    }
    this.recordingCompleted = true
    this.isActive = false
    this.updateButtonState(false)

    try {
      const endedAt = new Date()
      const durationMs = endedAt - new Date(this.startedAt)

      const transcript = this.currentSessionTexts ? this.currentSessionTexts.join('\n') : ''
      const mimeType = this.recordingMimeType || "audio/webm"
      const blob = new Blob(this.recordedChunks, { type: mimeType })
      const fixedBlob = await fixWebmDuration(blob, durationMs)

      const examinationId = this.examinationIdValue

      const result = await AudioRecordingService.save(examinationId, {
        startedAt: new Date(this.startedAt),
        endedAt: endedAt,
        transcript: transcript,
        audioFile: fixedBlob,
        createSoap: this.isCompleteRecording || false
      })

      if (result.success) {
        if (this.isCompleteRecording && this.qrEnabledValue) {
          this.isCompleteRecording = false
          window.location.href = window.location.pathname + '?show_qr=1'
        } else {
          this.isCompleteRecording = false
          window.location.reload()
        }
      } else {
        this.log(`録音データ保存エラー: ${result.error}`)
        this.isCompleteRecording = false
        this.enableCompleteButton()
      }
    } catch (error) {
      console.error('[Soniox handleRecordingComplete ERROR]', error)
      this.log(`録音完了処理エラー: ${error.message}`)
      this.isCompleteRecording = false
      this.enableCompleteButton()
    } finally {
      this._releaseStream()
    }
  }

  isRecording() {
    return this.isActive
  }

  getAutoRecordSetting() {
    const saved = localStorage.getItem('autoRecordEnabled')
    return saved !== null ? saved : 'false'
  }

  setupNavigationPrevention() {
    this.turboBeforeVisitHandler = (event) => {
      if (this.isRecording()) {
        event.preventDefault()
        alert('現在録音中のため、ページを移動できません。録音を停止してから再度お試しください。')
      }
    }
    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)

    this.beforeUnloadHandler = (event) => {
      if (this.isRecording()) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  }

  // --- Soniox制御 ---

  async _startSoniox(apiKey) {
    // ユーザー辞書（専門用語）を Soniox の terms として渡す
    const terms = Array.isArray(this.termsValue) ? this.termsValue.filter(t => typeof t === 'string' && t.length > 0) : []
    const context = terms.length > 0 ? { terms } : null

    this.sonioxClient = new SonioxStreamingClient({
      apiKey,
      languageHints: ["ja"],
      enableSpeakerDiarization: false,
      enableEndpointDetection: true,
      context,
      onPartial: (text, _speaker) => {
        this._renderPartial(text)
      },
      onFinal: (text, _speaker) => {
        this._appendFinal(text)
      },
      onEndpoint: () => {
        this._appendEndpointBreak()
      },
      onError: (error) => {
        console.error("[Soniox] ストリーミングエラー:", error)
      }
    })

    await this.sonioxClient.start(this.stream)
  }

  async _stopSoniox() {
    if (this.sonioxClient) {
      await this.sonioxClient.stop()
      this.sonioxClient = null
    }
    // partial 表示は破棄
    if (this.partialEl) {
      this.partialEl.remove()
      this.partialEl = null
    }
  }

  _appendFinal(text) {
    if (!text) return

    this.currentSessionTexts.push(text)

    if (this.hasHistoryTarget) {
      // partial 行があれば消す
      if (this.partialEl) {
        this.partialEl.remove()
        this.partialEl = null
      }
      const div = document.createElement('div')
      div.className = 'px-2 text-sm'
      div.style.lineHeight = '1.75'
      div.style.paddingTop = '0.125rem'
      div.style.paddingBottom = '0.125rem'
      div.textContent = text
      this.historyTarget.appendChild(div)
      this.scrollToBottom()
    }
  }

  // Soniox の <end> トークン（発話区切り）を受けたときに改行（空白）を挿入する
  _appendEndpointBreak() {
    // 保存される transcript にも改行を入れる
    if (this.currentSessionTexts && this.currentSessionTexts.length > 0) {
      const lastIndex = this.currentSessionTexts.length - 1
      if (!this.currentSessionTexts[lastIndex].endsWith("\n")) {
        this.currentSessionTexts[lastIndex] += "\n"
      }
    }

    // 画面側にも控えめな空白行を入れて発話の区切りを視覚的に示す
    if (this.hasHistoryTarget) {
      const sep = document.createElement('div')
      sep.style.height = '0.5em'
      sep.setAttribute('aria-hidden', 'true')
      this.historyTarget.appendChild(sep)
      this.scrollToBottom()
    }
  }

  _renderPartial(text) {
    if (!this.hasHistoryTarget) return

    if (!this.partialEl) {
      this.partialEl = document.createElement('div')
      this.partialEl.className = 'px-2 text-sm text-gray-400'
      this.partialEl.style.lineHeight = '1.75'
      this.partialEl.style.paddingTop = '0.125rem'
      this.partialEl.style.paddingBottom = '0.125rem'
      this.historyTarget.appendChild(this.partialEl)
    }
    this.partialEl.textContent = text
    this.scrollToBottom()
  }

  _releaseStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop())
      this.stream = null
    }
  }
}
